import { useMemo } from 'react';

import { FEEDBACK_COLUMNS_DEFAULT_WIDTH } from '@/Pages/DatasetSessionCompare/constants';
import { isRealRun } from '@/Pages/DatasetSessionCompare/utils/isRealRun';
import { useColorScheme } from '@/hooks/useColorScheme';
import {
  ExperimentViewOverridesSchema,
  FeedbackConfigSchema,
  RunSchema,
  SessionSchema,
} from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { AggregateFeedbackChips } from '../../AggregateFeedbackChips';
import { Loader } from '../Loader';
import { SingleExperimentCell } from '../SingleExperimentCell';
import { SingleFeedbackWithSource } from './SingleFeedbackWithSource';
import { useFeedbackStyling } from './hooks/feedback-styling';

export const FeedbackCell = ({
  feedbackKey,
  runs,
  mutateInfiniteExamples,
  datasetShareToken,
  expectedFeedbackKeys,
  columnIndex,
  columnWidthStorage,
  isTruncated,
  setOpenRunId,
  experimentInProgress,
  feedbackConfig,
  columnOverride,
  sessions,
  backgroundStyles,
}: {
  feedbackKey: string;
  runs: RunSchema[];
  mutateInfiniteExamples: () => void;
  datasetShareToken?: string;
  expectedFeedbackKeys?: string[];
  columnIndex: number;
  columnWidthStorage: number[];
  isTruncated: boolean;
  setOpenRunId: (runId: string | null) => void;
  experimentInProgress: boolean;
  feedbackConfig?: FeedbackConfigSchema;
  columnOverride?: ExperimentViewOverridesSchema['column_overrides'][number];
  sessions?: SessionSchema[];
  backgroundStyles: string;
}) => {
  const { isDarkMode } = useColorScheme();
  const allFeedbackStats = useMemo(
    () =>
      runs
        .filter(
          (
            run
          ): run is RunSchema & {
            feedback_stats: NonNullable<RunSchema['feedback_stats']>;
          } => !!run.feedback_stats?.[feedbackKey]
        )
        .map((run) => ({
          run_id: run.id,
          feedback_stats: run.feedback_stats[feedbackKey],
        })),
    [runs, feedbackKey]
  );

  const singleRunId = useMemo(() => {
    if (allFeedbackStats.length == 1) {
      return runs.find((run) => run.id === allFeedbackStats[0].run_id)!;
    }
    return null;
  }, [runs, allFeedbackStats]);

  const numErrors = allFeedbackStats.reduce(
    (sum, stats) => sum + (stats.feedback_stats?.errors ?? 0),
    0
  );
  const numN = allFeedbackStats.reduce(
    (sum, stats) =>
      sum +
      (stats.feedback_stats?.n ?? 0) +
      Object.values(stats.feedback_stats?.values ?? {}).reduce(
        (sum, value) => sum + (value ?? 0),
        0
      ),
    0
  );
  const showAggregateFeedbackChips = numErrors + numN > 1;

  const noFeedback = !(
    showAggregateFeedbackChips || allFeedbackStats.length === 1
  );
  // If there isn't a score, don't show heatmap styling
  const showHeatmap =
    !noFeedback &&
    (allFeedbackStats.length !== 1 || !!allFeedbackStats[0]?.feedback_stats?.n);

  const {
    text: textStyle,
    background: backgroundStyle,
    hover: hoverStyle,
  } = useFeedbackStyling({
    showHeatmap,
    allFeedbackStats: allFeedbackStats.map((fs) => ({
      feedback_stats: fs.feedback_stats,
    })),
    isDarkMode,
    feedbackConfig,
    columnOverride,
  });

  const isExpected = useMemo(
    () =>
      !allFeedbackStats.find((fs) =>
        Object.keys(fs.feedback_stats).includes(feedbackKey)
      ) && expectedFeedbackKeys?.includes(feedbackKey),
    [expectedFeedbackKeys, allFeedbackStats, feedbackKey]
  );

  // If there is only one run and it is not a real run, then we haven't actually run the experiment yet
  // We use fake runs for displaying errors pre-running the experiment
  // For new evaluators added after experiment completion, don't show loading state
  const isLoading =
    isExpected &&
    (experimentInProgress ||
      (runs.length > 0 &&
        !(runs.length === 1 && !isRealRun(runs[0]) && !runs[0].inputs)));

  const session = useMemo(
    () => sessions?.find((session) => session.id === singleRunId?.session_id),
    [sessions, singleRunId]
  );

  return (
    <SingleExperimentCell
      columnIndex={columnIndex}
      columnWidthStorage={columnWidthStorage}
      isTruncated={isTruncated}
      minColumnWidth={FEEDBACK_COLUMNS_DEFAULT_WIDTH}
      className={backgroundStyles}
    >
      <div className="rounded-md">
        {showAggregateFeedbackChips ? (
          <div className="flex flex-row gap-2">
            <AggregateFeedbackChips
              runs={runs}
              showSingleFeedbackKey={feedbackKey}
              allowTruncation
              hideFeedbackKey
              showErrorCount
              showErrorsOutside={showHeatmap}
              onMutate={mutateInfiniteExamples}
              disablePopover={!!datasetShareToken}
              expectedFeedbackKeys={expectedFeedbackKeys}
              onViewEvaluatorRun={(runId: string) => {
                setOpenRunId(runId);
              }}
              startDecorator={<></>}
              chipClassName={cn(
                'w-fit rounded-md border-none',
                showHeatmap && backgroundStyle,
                showHeatmap && hoverStyle
              )}
              valueClassName={showHeatmap ? textStyle : undefined}
              hideEllipsis={true}
              columnOverride={columnOverride}
            />
          </div>
        ) : allFeedbackStats.length === 1 ? (
          <SingleFeedbackWithSource
            feedbackKey={feedbackKey}
            run={singleRunId!}
            setOpenRunId={setOpenRunId}
            onMutate={mutateInfiniteExamples}
            chipClassName={cn(
              'rounded-md border-none',
              showHeatmap
                ? cn(backgroundStyle, hoverStyle, 'min-w-12 justify-center')
                : 'px-0'
            )}
            valueClassName={showHeatmap ? textStyle : undefined}
            isHeatmapVisible={showHeatmap}
            maxWidth={columnWidthStorage[columnIndex]}
            isTextTruncated={isTruncated}
            traceTier={session?.trace_tier}
            experimentStartTime={session?.start_time}
            columnOverride={columnOverride}
          />
        ) : isLoading ? (
          <Loader />
        ) : (
          <span className="flex text-sm text-ls-gray-200">No feedback</span>
        )}
      </div>
    </SingleExperimentCell>
  );
};
